import { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Package, Reception, ReceptionListItem } from '../../models/Reception';
import {
  CreateReceptionModal,
  DeleteReceptionModal,
  EditReceptionModal,
  IdentifyPackageModal,
  SplitPackageModal,
} from '../modals';

export interface ReceptionsModalsRef {
  openCreateModal: () => void;
  openEditModal: (reception: ReceptionListItem) => void;
  openDeleteModal: (reception: ReceptionListItem) => void;
  openIdentifyModal: (reception: Reception, pkg: Package) => void;
  openSplitModal: (reception: Reception, pkg: Package) => void;
}

/**
 * Contenedor de modales para el módulo de Recepciones
 * Gestiona de forma centralizada todos los modales
 * Nota: Para ver detalles de una recepción, navegar a /receptions/:id
 */
export const ReceptionsModals = forwardRef<ReceptionsModalsRef>((_, ref) => {
  // Referencias a los modales
  const createModalRef = useRef<ModalCenterRef>(null);
  const editModalRef = useRef<ModalCenterRef>(null);
  const deleteModalRef = useRef<ModalCenterRef>(null);
  const identifyModalRef = useRef<ModalCenterRef>(null);
  const splitModalRef = useRef<ModalCenterRef>(null);

  // Estado para los modales que necesitan datos
  const [selectedReception, setSelectedReception] = useState<ReceptionListItem | null>(null);
  const [selectedReceptionFull, setSelectedReceptionFull] = useState<Reception | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);

  // Estado para controlar qué modal abrir después del montaje
  const [pendingModal, setPendingModal] = useState<'edit' | 'delete' | null>(null);

  // Efecto para abrir el modal después de que se monte
  useEffect(() => {
    if (pendingModal === 'edit' && selectedReception) {
      // Pequeño delay para asegurar que el modal esté montado
      const timer = setTimeout(() => {
        editModalRef.current?.openModal();
        setPendingModal(null);
      }, 0);
      return () => clearTimeout(timer);
    }
    if (pendingModal === 'delete' && selectedReception) {
      const timer = setTimeout(() => {
        deleteModalRef.current?.openModal();
        setPendingModal(null);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [pendingModal, selectedReception]);

  // Handlers para abrir modales
  const openCreateModal = useCallback(() => {
    createModalRef.current?.openModal();
  }, []);

  const openEditModal = useCallback((reception: ReceptionListItem) => {
    setSelectedReception(reception);
    setPendingModal('edit');
  }, []);

  const openDeleteModal = useCallback((reception: ReceptionListItem) => {
    setSelectedReception(reception);
    setPendingModal('delete');
  }, []);

  const openIdentifyModal = useCallback((reception: Reception, pkg: Package) => {
    setSelectedReceptionFull(reception);
    setSelectedPackage(pkg);
    identifyModalRef.current?.openModal();
  }, []);

  const openSplitModal = useCallback((reception: Reception, pkg: Package) => {
    setSelectedReceptionFull(reception);
    setSelectedPackage(pkg);
    splitModalRef.current?.openModal();
  }, []);

  // Exponer métodos al padre
  useImperativeHandle(
    ref,
    () => ({
      openCreateModal,
      openEditModal,
      openDeleteModal,
      openIdentifyModal,
      openSplitModal,
    }),
    [openCreateModal, openEditModal, openDeleteModal, openIdentifyModal, openSplitModal],
  );

  return (
    <>
      {/* Modal Crear */}
      <CreateReceptionModal ref={createModalRef} />

      {/* Modal Editar */}
      {selectedReception && (
        <EditReceptionModal
          key={`edit-${selectedReception.id}`}
          ref={editModalRef}
          receptionId={selectedReception.id}
        />
      )}

      {/* Modal Eliminar */}
      {selectedReception && <DeleteReceptionModal ref={deleteModalRef} reception={selectedReception} />}

      {/* Modal Identificar Paquete */}
      {selectedReceptionFull && selectedPackage && (
        <IdentifyPackageModal ref={identifyModalRef} reception={selectedReceptionFull} pkg={selectedPackage} />
      )}

      {/* Modal Split Paquete */}
      {selectedReceptionFull && selectedPackage && (
        <SplitPackageModal ref={splitModalRef} reception={selectedReceptionFull} pkg={selectedPackage} />
      )}
    </>
  );
});

ReceptionsModals.displayName = 'ReceptionsModals';

export default ReceptionsModals;
